import { useState, useEffect, useCallback, type ComponentType, type SVGProps } from "react";
import { Refine, useGetIdentity, useTranslate } from "@refinedev/core";
import {
  AppShell,
  NavSection,
  NavItem,
  ProfileCard,
  DashboardIcon,
  HomeIcon,
  PawIcon,
  CalendarIcon,
  EnvelopeIcon,
  UserIcon,
  LockIcon,
  IdCardIcon,
  BuildingIcon,
  GearIcon,
  ShieldIcon,
} from "@fawkes/ui";

import { dataProvider } from "./dataProvider";
import { authProvider, currentRole, currentRoles, restoreSession } from "./authProvider";
import { LoginPage } from "./pages/auth/LoginPage";
import { SetPasswordPage } from "./pages/auth/SetPasswordPage";
import { Dashboard } from "./pages/portal/Dashboard";
import { PortalAnimalDetail } from "./pages/portal/PortalAnimalDetail";
import { PhoneIcon } from "./pages/portal/icons";
import { MijnDieren } from "./pages/portal/MijnDieren";
import { Beschikbaarheid } from "./pages/portal/Beschikbaarheid";
import { Contact } from "./pages/portal/Contact";
import { MijnGegevens } from "./pages/portal/MijnGegevens";
import { FosterShell } from "./shell/FosterShell";
import { Users } from "./pages/admin/Users";
import { Persons } from "./pages/admin/Persons";
import { ServiceAccounts, ServiceAccountDetail } from "./pages/admin/ServiceAccounts";
import { Organizations } from "./pages/admin/Organizations";
import { OrganizationRotation } from "./pages/admin/OrganizationRotation";
import { MijnServiceAccount } from "./pages/selfservice/MijnServiceAccount";
import { isSetPasswordPath } from "./auth/setPasswordRoute";
import { isAfstandPath } from "./afstand/afstandRoute";
import { AfstandPortaal } from "./pages/afstand/AfstandPortaal";
import { isNazorgPath } from "./nazorg/nazorgRoute";
import { NazorgPortal } from "./pages/nazorg/NazorgPortal";
import { DierMatchSite } from "./pages/public/DierMatchSite";
import { resolveSite } from "./public/publicSite";
import { IkZoekBaasSite } from "./pages/public/IkZoekBaasSite";
import {
  isLoginPath,
  loginModeForPath,
  loginPathForPath,
  loginPathForRole,
} from "./auth/loginRoute";
import { i18nProvider } from "./i18n/i18nProvider";
import { Placeholder, EmptyState } from "./components/states";
import {
  NAV,
  visibleNav,
  landingPageForRole,
  initialPageFromPath,
  PAGE_TITLE_KEY,
  type Page,
  type Role,
} from "./shell/nav";

// jmix-exit E3a.3: the application shell. Reproduces the Jmix MainView + menu.xml on the shared
// @fawkes/ui AppShell: role-filtered sidebar (foster / admin / self-service), role-based landing
// parity, and the error/empty/loading conventions in the working area. State-based navigation; every
// screen is a Placeholder until Epics 7–9 build it (big-bang ⇒ no legacy Jmix hand-off).
//
// DELIBERATE DEVIATIONS (vs. menu.xml) — see src/shell/nav.ts for the full note:
//   • Dropped: security/resourceRoles + rowLevelRoles (Jmix sec-model editors → Spring RBAC, Epic 5)
//     and the whole datatools group (Jmix tooling, decommissioned in E10). No app screen behind them.
//   • Auth (jmix-3b.1): the real /api/auth/* bridge — the Gate boots via silent restore (httpOnly
//     refresh cookie), shows LoginPage when out, and derives the nav Role from the JWT authorities
//     (roleFromAuthorities), replacing the Epic-3a dev role-switch.

type IconComp = ComponentType<SVGProps<SVGSVGElement>>;

// Icon key (nav.ts, React-free) → @fawkes/ui icon component. Shared, tintable, theme-aware glyphs —
// no Jmix asset copying (unlike backoffice, which reused the legacy theme's PNG/SVG glyphs).
const ICON_BY_KEY: Record<string, IconComp> = {
  dashboard: DashboardIcon,
  paw: PawIcon,
  calendar: CalendarIcon,
  envelope: EnvelopeIcon,
  user: UserIcon,
  lock: LockIcon,
  users: UserIcon,
  idcard: IdCardIcon,
  building: BuildingIcon,
  rotate: GearIcon,
  shield: ShieldIcon,
};

function icon(key: string | undefined, size = 18) {
  if (!key) return undefined;
  const Comp = ICON_BY_KEY[key];
  return Comp ? <Comp width={size} height={size} /> : undefined;
}

// fix/foster-login-redesign: the FosterShell sidebar uses the design's glyphs — Home for Dashboard and
// a Phone for Contact (the nav 'envelope' key) — overriding the shared ICON_BY_KEY for the foster portal
// only (admin / self-service keep the AppShell icons).
const FOSTER_ICON_BY_KEY: Record<string, IconComp> = {
  dashboard: HomeIcon,
  paw: PawIcon,
  calendar: CalendarIcon,
  envelope: PhoneIcon,
  user: UserIcon,
};
function fosterIcon(key: string | undefined) {
  if (!key) return undefined;
  const Comp = FOSTER_ICON_BY_KEY[key] ?? ICON_BY_KEY[key];
  return Comp ? <Comp width={22} height={22} /> : undefined;
}

/** The sidebar brand sub-text mirrors MainView: foster portal / self-service / admin. */
function brandSubKey(role: Role): string {
  if (role === "self-service") return "shell.applicationTitle.selfservice";
  if (role === "admin") return "shell.applicationTitle.admin";
  return "shell.applicationTitle";
}

function SidebarProfile({ onLogout }: { onLogout: () => void }) {
  const t = useTranslate();
  const { data: identity } = useGetIdentity<{ name?: string; email?: string }>();
  return (
    <ProfileCard
      name={identity?.name ?? t("shell.brand")}
      email={identity?.email}
      settingsLabel={t("shell.profile.settings")}
      logoutLabel={t("shell.profile.logout")}
      onLogout={onLogout}
    />
  );
}

function AuthedApp(
  { role, roles, onLogout }: { role: Role; roles: readonly Role[]; onLogout: () => void },
) {
  const t = useTranslate();
  const [page, setPage] = useState<Page>(() => {
    const path = globalThis.location?.pathname ?? "/";
    const normalized = path.replace(/\/+$/, "");
    // Root → role-based landing (MainView parity); a deep link → that page.
    return normalized === "" ? landingPageForRole(role) : initialPageFromPath(path);
  });

  // The service-account detail is a dedicated page reached from the list; remember which account so the
  // detail view can fetch it, and keep the "Serviceaccounts" nav item highlighted while it's open.
  const [serviceAccountId, setServiceAccountId] = useState<string | null>(null);
  const openServiceAccount = (id: string) => {
    setServiceAccountId(id);
    setPage("service-account-detail");
  };

  // jmix-8.5: the foster animal-detail is a dedicated page reached from Mijn dieren / Dashboard; remember
  // which animal so the detail view can fetch it, and keep "Mijn dieren" highlighted while it's open.
  const [animalRef, setAnimalRef] = useState<{ organizationUuid: string; dierUuid: string } | null>(null);
  const openAnimal = (organizationUuid: string, dierUuid: string) => {
    setAnimalRef({ organizationUuid, dierUuid });
    setPage("animal-detail");
  };

  // Keep the document title in sync with the active page (a11y: screen readers announce the page; and
  // the tab is no longer permanently labelled "Pleeggezinportaal" on admin/self-service screens).
  useEffect(() => {
    document.title = `${t(PAGE_TITLE_KEY[page])} · ${t("shell.brand")}`;
  }, [page, t]);

  // jmix review 3a-T2: nav is additive over ALL the principal's roles (Jmix menu was additive-by-
  // policy); landing + brand still key off the primary role (the precedence winner).
  const groups = visibleNav(roles);

  const nav = groups.map((group) => (
    <NavSection
      key={group.id}
      label={t(group.titleKey)}
      defaultOpen={group.mode === "open"}
      icon={icon(group.sectionIcon, 16)}
    >
      {group.items.map((item) => (
        <NavItem
          key={item.page}
          label={t(item.labelKey)}
          icon={icon(item.icon)}
          active={
            page === item.page ||
            (item.page === "service-accounts" && page === "service-account-detail")
          }
          onClick={() => setPage(item.page)}
        />
      ))}
    </NavSection>
  ));

  const working = (
    <Working
      page={page}
      onHome={() => setPage(landingPageForRole(role))}
      onNavigate={setPage}
      serviceAccountId={serviceAccountId}
      onOpenServiceAccount={openServiceAccount}
      onOpenAnimal={openAnimal}
      animalRef={animalRef}
    />
  );

  // Foster role → the bespoke portal shell (mockup design); admin / self-service keep the AppShell.
  if (role === "foster") {
    const fosterItems = groups.flatMap((group) => group.items).map((item) => ({
      page: item.page,
      label: t(item.labelKey),
      icon: fosterIcon(item.icon),
      active: page === item.page || (item.page === "my-animals" && page === "animal-detail"),
      onClick: () => setPage(item.page),
    }));
    return (
      <FosterShell items={fosterItems} onLogout={onLogout}>
        {working}
      </FosterShell>
    );
  }

  return (
    <AppShell
      brandTitle={t("shell.brand")}
      title={t(PAGE_TITLE_KEY[page])}
      locationSelector={<div className="fawkes-brand-sub">{t(brandSubKey(role))}</div>}
      nav={nav}
      sidebarFooter={<SidebarProfile onLogout={onLogout} />}
    >
      {working}
    </AppShell>
  );
}

// The working area. Epic 3a rendered the shared conventions; Epic 8 swapped the five FOSTER pages and
// Epic 9 swaps the admin pages for real Refine screens. `home` is the admin overview landing; any
// not-yet-built page is still a Placeholder. jmix-9-2: `self-service` is now the real service-account
// self-service portal (over /api/portal/service-account/**) — a web-portal SA logs in at /aerie and
// lands here via landingPageForRole. The interactive-login invariant change lives in AppUserDetailsService.
function Working(
  {
    page,
    onHome,
    onNavigate,
    serviceAccountId,
    onOpenServiceAccount,
    onOpenAnimal,
    animalRef,
  }: {
    page: Page;
    onHome: () => void;
    onNavigate: (page: Page) => void;
    serviceAccountId: string | null;
    onOpenServiceAccount: (id: string) => void;
    onOpenAnimal: (organizationUuid: string, dierUuid: string) => void;
    animalRef: { organizationUuid: string; dierUuid: string } | null;
  },
) {
  const t = useTranslate();
  switch (page) {
    case "home":
      return <EmptyState title={t("shell.home.title")} body={t("shell.home.body")} />;
    // jmix-8: the foster portal.
    case "dashboard":
      return <Dashboard onNavigate={onNavigate} onOpenAnimal={onOpenAnimal} />;
    case "my-animals":
      return <MijnDieren onOpenAnimal={onOpenAnimal} onNavigate={onNavigate} />;
    case "animal-detail":
      return animalRef ? (
        <PortalAnimalDetail
          key={`${animalRef.organizationUuid}:${animalRef.dierUuid}`}
          organizationUuid={animalRef.organizationUuid}
          dierUuid={animalRef.dierUuid}
          onBack={() => onNavigate("my-animals")}
          onNavigate={onNavigate}
          onOpenAnimal={onOpenAnimal}
        />
      ) : (
        <MijnDieren onOpenAnimal={onOpenAnimal} />
      );
    case "availability":
      return <Beschikbaarheid onNavigate={onNavigate} />;
    case "contact":
      return <Contact onNavigate={onNavigate} />;
    case "my-profile":
      return <MijnGegevens onNavigate={onNavigate} />;
    // jmix-9: the admin console.
    case "users":
      return <Users />;
    case "persons":
      return <Persons />;
    case "service-accounts":
      return <ServiceAccounts onOpen={onOpenServiceAccount} />;
    case "service-account-detail":
      return serviceAccountId ? (
        <ServiceAccountDetail id={serviceAccountId} onBack={() => onNavigate("service-accounts")} />
      ) : (
        <ServiceAccounts onOpen={onOpenServiceAccount} />
      );
    case "organizations":
      return <Organizations />;
    case "organization-rotation":
      return <OrganizationRotation />;
    // jmix-9-2: the service-account self-service portal.
    case "self-service":
      return <MijnServiceAccount />;
    default:
      return <Placeholder title={t(PAGE_TITLE_KEY[page])} onHome={onHome} />;
  }
}

// jmix-3b.1: the auth gate. On mount it attempts a silent restore from the httpOnly refresh cookie,
// then derives the nav Role from the in-memory JWT authorities: a recognised role → the shell; none →
// a login screen. `onAuthenticated` re-syncs after a successful login; logout clears the token
// (best-effort server revoke is jmix-3b-2) and drops back to login.
//
// Login split: there are TWO login routes, resolved from the address bar (auth/loginRoute.ts) —
// /aerie (admin password) and /login (foster magic code). A logged-out deep link is canonicalised to
// the matching login route (an admin route → /aerie, a foster route → /login, bare/unknown → /login),
// while the ORIGINAL deep link is remembered so a successful login returns there (a login route itself
// carries no deep-link intent → role landing). Logout returns the principal to their persona's login.
type GatePhase = "booting" | "in" | "out";

/** Replace the address bar without a navigation (no router); a no-op where history is unavailable. */
function replacePath(path: string) {
  globalThis.history?.replaceState?.(null, "", path);
}

function Gate() {
  const t = useTranslate();
  // jmix-7-3: /set-password is a standalone unauthenticated landing page reached via an admin-issued
  // reset link — it must NOT run the auth gate (no silent restore, no login redirect). Resolved once.
  const [setPwMode] = useState(() => isSetPasswordPath(globalThis.location?.pathname));
  // The adopter nazorg portal (/nazorg/<token>) is a standalone unauthenticated landing like
  // /set-password — the token IS the credential, so it must bypass the auth gate entirely.
  const [nazorgMode] = useState(() => isNazorgPath(globalThis.location?.pathname));
  // Het afstandsportaal (/afstand/<opvangsleutel>) is net zo'n losstaande landing zonder inlog: de
  // eigenaar die zijn dier aanmeldt heeft geen account bij dit platform, en de authenticatiepoort van
  // de app mag hem niet naar een loginscherm sturen dat niet voor hem bedoeld is.
  const [afstandMode] = useState(() => isAfstandPath(globalThis.location?.pathname));
  // The public DierMatch site (/dieren, /dieren/<slug>) is a standalone unauthenticated landing too —
  // anonymous adoption browsing, no login, so it bypasses the auth gate entirely.
  // Both public adoption sites are selected synchronously from host + path, before session restore.
  // On their own domains even `/` belongs to the public brand, never to the app login.
  const [publicSiteMode] = useState(() =>
    resolveSite(globalThis.location?.hostname, globalThis.location?.pathname),
  );
  const dierMatchMode = publicSiteMode === "DIERMATCH";
  const ikZoekBaasMode = publicSiteMode === "IKZOEKBAAS";
  const [phase, setPhase] = useState<GatePhase>("booting");
  const [role, setRole] = useState<Role | null>(null);
  const [roles, setRoles] = useState<readonly Role[]>([]);
  // The path the visitor arrived on, captured before any canonicalisation. A successful login returns
  // here (deep-link preservation); a login route itself means "no deep link" → the role landing.
  const [intendedPath, setIntendedPath] = useState(() => globalThis.location?.pathname ?? "/");
  // The canonical login route to show while out (drives which form + the address bar).
  const [loginPath, setLoginPath] = useState(() => loginPathForPath(globalThis.location?.pathname));

  const sync = useCallback(() => {
    const resolved = currentRole();
    if (resolved) {
      setRole(resolved);
      setRoles(currentRoles());
      setPhase("in");
    } else {
      setRole(null);
      setRoles([]);
      setPhase("out");
    }
  }, []);

  useEffect(() => {
    if (setPwMode || nazorgMode || afstandMode || dierMatchMode || ikZoekBaasMode) {
      return; // standalone unauthenticated landing pages own their own flow — skip the session restore
    }
    let active = true;
    void (async () => {
      await restoreSession();
      if (active) {
        sync();
      }
    })();
    return () => {
      active = false;
    };
  }, [sync, setPwMode, nazorgMode, afstandMode, dierMatchMode, ikZoekBaasMode]);

  // Once we know the visitor is logged out, canonicalise a deep link to its login route (the "redirect
  // to /aerie | /login"). Only when out — a successful silent restore keeps the deep link for AuthedApp.
  useEffect(() => {
    if (phase === "out") {
      replacePath(loginPath);
    }
  }, [phase, loginPath]);

  // Outside the authed shell (booting / login / set-password) keep the tab on the brand name;
  // AuthedApp owns the page-specific "<Page> · Docasoft" title once signed in.
  //
  // The standalone public sites are excluded: they are not this product to their visitors, and a
  // parent effect runs AFTER its children's, so without this guard it would overwrite the title the
  // public site just set and leave an adoption page labelled with our company name.
  const publiekeSite = nazorgMode || afstandMode || dierMatchMode || ikZoekBaasMode;
  useEffect(() => {
    if (phase !== "in" && !publiekeSite) {
      document.title = t("shell.brand");
    }
  }, [phase, t, publiekeSite]);

  // A successful login: restore the remembered deep link (or the role landing for a login route) so
  // AuthedApp resolves the right initial page from the address bar, then re-sync to render the shell.
  const onAuthenticated = useCallback(() => {
    replacePath(isLoginPath(intendedPath) ? "/" : intendedPath);
    sync();
  }, [intendedPath, sync]);

  // Before dierMatchMode: on an ikzoekbaas host the visitor is on that site whatever the path says.
  if (ikZoekBaasMode) {
    return <IkZoekBaasSite />;
  }
  if (dierMatchMode) {
    return <DierMatchSite />;
  }
  if (nazorgMode) {
    return <NazorgPortal />;
  }
  if (afstandMode) {
    return <AfstandPortaal />;
  }
  if (setPwMode) {
    return <SetPasswordPage />;
  }
  if (phase === "booting") {
    return <div className="fawkes-auth fawkes-auth--booting">{t("state.loading")}</div>;
  }
  if (phase === "out" || role === null) {
    return (
      <LoginPage
        mode={loginModeForPath(loginPath)}
        onAuthenticated={onAuthenticated}
      />
    );
  }
  return (
    <AuthedApp
      key={role}
      role={role}
      roles={roles}
      onLogout={() => {
        const dest = loginPathForRole(role);
        void (async () => {
          await authProvider.logout?.({});
          replacePath(dest);
          setLoginPath(dest);
          setIntendedPath(dest);
          sync();
        })();
      }}
    />
  );
}

export default function App() {
  return (
    <Refine
      dataProvider={dataProvider}
      authProvider={authProvider}
      i18nProvider={i18nProvider}
      resources={[
        { name: "admin/users" }, // jmix-9.1: the admin user list (useList resource → /api/admin/users)
        { name: "admin/persons" }, // read-only PERSON list (→ /api/admin/persons)
        { name: "admin/service-accounts" }, // jmix-9.2
        { name: "admin/organizations" }, // jmix-9.3
        { name: "foster-absences" },
        { name: "portal/absences" }, // jmix-8.3: the foster portal absence list (useList resource)
      ]}
      options={{ disableTelemetry: true, warnWhenUnsavedChanges: false }}
    >
      <Gate />
    </Refine>
  );
}

// Re-export the menu model for the structure-lock test (App.nav.test.ts).
export { NAV, visibleNav, landingPageForRole, initialPageFromPath };
